import { useClientPortalAPI } from "@certn/api";
import {
  Container,
  Layout,
  Loader,
  SecondaryTopNav,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { createIncludesTabIdMapper } from "@certn/utils";

import { BillingProfileDetailsHeadingCard } from "./BillingProfileDetailsHeadingCard";
import { ClientBillingProfileContactInformationForm } from "./ClientBillingProfileContactInformationForm";
import { ClientBillingProfileDetailsForm } from "./ClientBillingProfileDetailsForm";
import { ClientBillingProfileGroupsForm } from "./ClientBillingProfileGroupsForm";
import { usePermissions } from "../../../components/permissions/PermissionsProvider";
import { PageTitle } from "../components/PageTitle";

export function BillingProfileDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const url = useResolvedPath("").pathname;
  const { pathname } = useLocation();
  const { billingProfileId } = useParams();
  const { data: billingProfile } = useClientPortalAPI(
    `/api/internal/client-portal/billing-profiles/${billingProfileId}/`
  );

  const { hasPermission } = usePermissions();

  const mapSelectedTabId = createIncludesTabIdMapper(
    [
      { substring: "/groups", id: "groups-tab" },
      { substring: "/contact-information", id: "contact-information-tab" },
    ],
    "details-tab"
  );
  const userHasBillingProfileUpdatePermission = hasPermission({
    action: "update",
    target: "billing_profile",
    scope: "all",
  });

  if (!billingProfile) {
    return <Loader />;
  }

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="back-button"
          leftIcon="arrow_back"
          onleftIconOnPress={() => navigate(`/billing/billing-profiles`)}
          titleArea={{ title: t("Billing profiles") }}
        />
      }
    >
      <Container>
        <PageTitle>
          <Trans>Billing Profile Details</Trans>
        </PageTitle>

        <BillingProfileDetailsHeadingCard
          billingProfileName={billingProfile.profile_name || ""}
          billingProfileId={billingProfileId || ""}
          archived={billingProfile.archived}
        />
        <Tabs
          key={url}
          gap="space-0"
          selectedKey={mapSelectedTabId(pathname)}
          aria-label="billing-profile-tabs"
          testID="billing-profile-tabs"
        >
          <TabList aria-label="Tabs" className={styles.tabList}>
            <Tab id="details-tab" href={url} testID="details-tab">
              {t("Details")}
            </Tab>
            <Tab id="groups-tab" href={`${url}/groups`} testID="groups-tab">
              {t("Groups")}
            </Tab>
            <Tab
              id="contact-information-tab"
              href={`${url}/contact-information`}
              testID="contact-information-tab"
            >
              {t("Contact information")}
            </Tab>
          </TabList>
          <TabPanel id={mapSelectedTabId(pathname)} className={styles.tabPanel}>
            <Routes>
              <Route
                path="/"
                element={
                  <ClientBillingProfileDetailsForm
                    readOnly={!userHasBillingProfileUpdatePermission}
                  />
                }
              />
              <Route
                path="/groups"
                element={<ClientBillingProfileGroupsForm />}
              />
              <Route
                path="/contact-information"
                element={
                  <ClientBillingProfileContactInformationForm
                    readOnly={!userHasBillingProfileUpdatePermission}
                  />
                }
              />
            </Routes>
          </TabPanel>
        </Tabs>
      </Container>
    </Layout>
  );
}

const styles = {
  flexContainer: css({
    display: "flex",
    gap: "var(--space-2)",
    alignItems: "center",
  }),
  tabList: css({
    backgroundColor: "var(--surface-base)",
    paddingTop: "var(--space-4)",
  }),
  tabPanel: css({
    paddingTop: "var(--space-5)",
  }),
};
